import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { mainConfig } from '@config/main.config';
import { Role } from '@models/role.model';
import { RolesService } from '@services/roles.service';
import { UtilsService } from '@services/utils.service';
import { PageRequest } from '@sinapsys/ngx-crud';
import { ConfermaService } from '@shared/conferma/conferma-dialog.component';
import { ColumnDefinition } from '@shared/data-table/column-definition';
import { DataTableComponent } from '@shared/data-table/data-table.component';
import { Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { PageComponent } from '../../../layout/page/page.component';

@Component({
  selector: 'app-roles',
  standalone: true,
  imports: [DataTableComponent, PageComponent, mainConfig.material, mainConfig.formModules],
  templateUrl: './roles.component.html',
})
export class RolesComponent implements OnInit, OnDestroy {
  @ViewChild(DataTableComponent, { static: false }) table!: DataTableComponent<Role>;
  @ViewChild('detailsModal', { static: true }) detailsModal!: TemplateRef<unknown>;
  @ViewChild('codiceCella', { static: true }) codiceCella!: TemplateRef<unknown>;
  @ViewChild('azioniCella', { static: true }) azioniCella!: TemplateRef<unknown>;

  columnDefinitions: ColumnDefinition<Role>[] = [];
  searchSubscription?: Subscription;

  readonly filtroTesto = new FormControl<string>('', { nonNullable: true });

  readonly caricamento = signal(false);
  readonly errore = signal<string | null>(null);
  readonly totale = signal<number | null>(null);

  editItemForm: FormGroup;
  editItem?: Role;
  dialogRef?: MatDialogRef<unknown>;

  private readonly sottoscrizioni = new Subscription();

  constructor(
    public utilsService: UtilsService,
    private rolesService: RolesService,
    private dialog: MatDialog,
    private conferma: ConfermaService
  ) {
    this.editItemForm = new FormGroup({
      id: new FormControl<number | undefined>(undefined),
      code: new FormControl<string | undefined>(undefined),
      description: new FormControl<string | undefined>(undefined),
    });
  }

  ngOnDestroy(): void {
    this.sottoscrizioni.unsubscribe();
  }

  ngOnInit(): void {
    this.columnDefinitions = [
      { id: 'description', header: 'Ruolo', sortable: true },
      { id: 'code', header: 'Codice', sortable: true, template: this.codiceCella, width: '180px' },
      { id: 'id', header: 'ID', sortable: true, width: '90px', nascondiSuMobile: true },
      { id: '_', header: '', template: this.azioniCella, width: '56px', allineamento: 'fine' },
    ];

    this.sottoscrizioni.add(
      this.filtroTesto.valueChanges.pipe(debounceTime(350), distinctUntilChanged()).subscribe(() => this.search())
    );
  }

  search(mantieniPagina?: boolean) {
    if (!this.table) return;

    if (!this.table.sort.active) {
      this.table.sort.active = 'id';
      this.table.sort.direction = 'asc';
    }
    if (!mantieniPagina) this.table.paginator.pageIndex = 0;

    // Una sola casella cerca sia nel codice sia nella descrizione: prima erano
    // due campi separati, e chi cercava "owner" nel campo sbagliato non
    // trovava niente.
    const cercato = this.filtroTesto.value.trim();
    const q = cercato ? { $or: { code: { $like: cercato }, description: { $like: cercato } } } : {};

    const pageRequest = new PageRequest(
      this.table.paginator.pageIndex,
      this.table.paginator.pageSize,
      this.table.sort.direction,
      this.table.sort.active
    );

    this.searchSubscription?.unsubscribe();
    this.caricamento.set(true);
    this.errore.set(null);

    this.searchSubscription = this.rolesService.search(q, pageRequest).subscribe({
      next: (res) => {
        res.items = res.items.map((item) => new Role(item));
        this.table.populate(res);
        this.totale.set(res.total);
        this.caricamento.set(false);
      },
      error: (err) => {
        this.caricamento.set(false);
        this.totale.set(null);
        this.errore.set(err?.error?.message || err?.error?.messaggio || err?.message || 'Errore inatteso');
        this.utilsService.defaultHttpErrorHandler(err);
      },
    });
  }

  azzeraFiltri(): void {
    this.filtroTesto.setValue('', { emitEvent: false });
    this.search();
  }

  sottotitolo(): string {
    const totale = this.totale();
    if (totale === null) return '';
    if (totale === 0) return this.filtroTesto.value.trim() ? 'Nessun ruolo con questa ricerca' : 'Nessun ruolo';
    return totale === 1 ? '1 ruolo' : `${totale} ruoli`;
  }

  new() {
    this.edit(new Role());
  }

  edit(item: Role) {
    this.editItemForm.reset();
    this.editItemForm.patchValue(item);
    this.editItem = item;
    this.dialogRef = this.dialog.open(this.detailsModal, { width: '520px' });
  }

  save() {
    const raw = this.editItemForm.getRawValue();
    this.rolesService.save(raw).subscribe({
      next: () => {
        this.utilsService.notificationsService.showNotification('', 'Ruolo salvato', 'success');
        this.search(true);
        this.dialogRef?.close();
      },
      error: (err) => this.utilsService.defaultHttpErrorHandler(err),
    });
  }

  async elimina() {
    const descrizione = this.editItemForm.controls['description'].value ?? '';
    const procedi = await this.conferma.chiedi({
      titolo: 'Eliminare questo ruolo?',
      testo: `"${descrizione}" verrà rimosso. Chi lo aveva perderà i permessi che ne derivano.`,
      conferma: 'Elimina',
      distruttiva: true,
    });
    if (!procedi) return;

    this.rolesService.delete(this.editItemForm.controls['id'].value).subscribe({
      next: () => {
        this.utilsService.notificationsService.showNotification('', 'Ruolo eliminato', 'success');
        this.search(true);
        this.dialogRef?.close();
      },
      error: (err) => this.utilsService.defaultHttpErrorHandler(err),
    });
  }
}
